import { useCallback, useMemo } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { selectedFrequencyFinancialsState } from '../../FinancialsDatePickers/useFinancialsDateSettings';
import { KpiPeriod } from '../../../../../data-models/company-financials.data-model';
import { selectedFinancialSettingsFieldsOrgState } from '../../../../AdminPanel/DataDisplay/DataDisplayState';

export interface IFinancialsDisplayOptions {
  showBudget?: boolean;
  showLTM?: boolean;
  showYTD?: boolean;
  showGrowth?: boolean;
}
export type FinancialsSettingsKey = keyof IFinancialsDisplayOptions | 'reportedOnly' | 'showPinnedYears';

export const settingsKeyToLabelMap: Record<FinancialsSettingsKey, string> = {
  reportedOnly: 'Only Reported KPIs',
  showBudget: 'Show Budget',
  showLTM: 'Show LTM',
  showYTD: 'Show YTD',
  showGrowth: 'Show Delta Trend',
  showPinnedYears: 'Show Years',
};

export type FinancialsDisplaySettings = Record<FinancialsSettingsKey, boolean>;

export function useFinancialsGridSettings() {
  const [selectedFields, setSelectedFields] = useAtom(selectedFinancialSettingsFieldsOrgState);
  const selectedPeriod = useAtomValue(selectedFrequencyFinancialsState);
  const handleSettingsChange = useCallback(
    (key: FinancialsSettingsKey) => {
      switch (key) {
        case 'reportedOnly':
          setSelectedFields(
            selectedFields.includes('reportedOnly')
              ? selectedFields.filter((f) => f !== 'reportedOnly')
              : [...selectedFields, 'reportedOnly']
          );
          break;
        case 'showBudget':
          setSelectedFields(
            selectedFields.includes('showBudget')
              ? selectedFields.filter((f) => f !== 'showBudget')
              : [...selectedFields, 'showBudget']
          );
          break;
        case 'showLTM':
          setSelectedFields(
            selectedFields.includes('showLT')
              ? selectedFields.filter((f) => f !== 'showLT')
              : [...selectedFields, 'showLT']
          );
          break;
        case 'showYTD':
          setSelectedFields(
            selectedFields.includes('showYTD')
              ? selectedFields.filter((f) => f !== 'showYTD')
              : [...selectedFields, 'showYTD']
          );
          break;
        case 'showGrowth':
          setSelectedFields(
            selectedFields.includes('showDeltaTrend')
              ? selectedFields.filter((f) => f !== 'showDeltaTrend')
              : [...selectedFields, 'showDeltaTrend']
          );
          break;
      }
    },
    [setSelectedFields, selectedFields]
  );

  const showPinnedYears = useMemo(() => {
    return selectedPeriod !== KpiPeriod.year;
  }, [selectedPeriod]);

  const displaySettings: FinancialsDisplaySettings = useMemo(() => {
    return {
      reportedOnly: selectedFields.includes('reportedOnly'),
      showBudget: selectedFields.includes('showBudget'),
      showLTM: selectedFields.includes('showLT'),
      showYTD: selectedFields.includes('showYTD'),
      showGrowth: selectedFields.includes('showDeltaTrend'),
      showPinnedYears,
    };
  }, [selectedFields, showPinnedYears]);

  return {
    displaySettings,
    handleSettingsChange,
  };
}
